import type { LoaderFunction, LoaderFunctionArgs, NavigationType, RouteObject } from 'react-router'
import type { Store } from 'redux'
import { isAxiosError } from 'axios'
import loadable from '@loadable/component'

import {
  getDefaultThemeSettings,
  getScriptTags,
  getTax,
  loadFooterPages,
  loadLegalPages,
  loadNavigation,
} from './store/actions'
import { mapDeep } from './utils/treeHelpers'
import { parseUrlQuery } from '../utils/parseUrlQuery'
import Category from './views/Category'
import ErrorBoundary from './views/ErrorBoundary'
import Home from './views/Home'
import LegalPage from './views/LegalPage'
import Order from './views/Order'
import Page from './views/Page'
import RootView from './views/RootView'
import Search from './views/Search'
import Storefront from './views/Storefront'
import UnknownRoute from './views/UnknownRoute'
import runAllViewStoreUpdates from '../web/utils/runAllViewStoreUpdates'

export const cartPath = 'cart'

export const orderPath = 'o'

const Account = loadable(() => import(/* webpackChunkName: "account" */ './views/Account'))
const Product = loadable(() => import(/* webpackChunkName: "Product" */ './views/Product'))
const Cart = loadable(() => import(/* webpackChunkName: "Cart" */ './views/Cart'))
const Checkout = loadable(() => import(/* webpackChunkName: "checkout" */ './views/Checkout'))
const ForgotPassword = loadable(() => import(/* webpackChunkName: "checkout" */ './views/ForgotPassword'))
const ResetPassword = loadable(() => import(/* webpackChunkName: "checkout" */ './views/ResetPassword'))
const Editor = loadable(() => import(/* webpackChunkName: "editor" */ './views/Editor'))
const Themes = loadable(() => import(/* webpackChunkName: "editor" */ './views/editor/Themes'))

export default function generateRoutes(store: Store): RouteObject[] {
  const locales: Core.Locale[] = store.getState().getIn(['shop', 'locales']).toJS()
  const availableLanguages = locales.map(({ identifier }) => identifier.substring(0, 2))
  const state = store.getState()
  const locale = state.getIn(['shop', 'locale'])

  const localizedStorefrontRoutes = {
    path: ':lang?',
    children: [
      {
        Component: Storefront,
        ErrorBoundary,
        loader: withLoaders(async ({ params }) => {
          if (!state.get('defaultThemeSettings')) {
            await dispatch(getDefaultThemeSettings(state.getIn(['shop', 'themeId'])))
          }

          if (params.lang && !availableLanguages.includes(params.lang)) {
            throw new Response('Not found', { status: 404 })
          }

          return null
        }),
        children: [
          {
            index: true,
            Component: Home,
          },
          { path: 'p/:productId', Component: Product },
          { path: 'c/*', Component: Category },
          { path: 'i/:pageSlug', Component: Page },
          { path: 'l/:legalPageSlug', Component: LegalPage },
          { path: cartPath, Component: Cart },
          { path: `${orderPath}`, Component: Order },
          { path: `${orderPath}/:orderId`, Component: Order },
          { path: 'search', Component: Search },
          {
            path: 'checkout',
            Component: Checkout,
          },
          {
            // todo refactor to using outlet
            path: 'checkout/:stepId',
            Component: Checkout,
          },
          { path: 'forgot-password', Component: ForgotPassword },
          { path: 'reset-password', Component: ResetPassword },
          { path: 'customer-account', Component: Account },
          { path: 'customer-account/:section', Component: Account },
          {
            path: '*',
            Component: UnknownRoute,
            loader: withLoaders(() => {
              throw new Response('Not found', { status: 404 })
            }),
          },
        ],
      },
    ],
  }

  const routes: RouteObject[] = [
    {
      path: '/',
      Component: RootView,
      ErrorBoundary,
      loader: () => null,
      children: [
        localizedStorefrontRoutes,
        {
          id: 'editor',
          Component: Editor,
          path: 'editor',
          children: [{ path: 'themes', Component: Themes }, localizedStorefrontRoutes],
        },
        {
          path: '*',
          Component: UnknownRoute,
          loader: () => {
            throw new Response('Not found', { status: 404 })
          },
        },
      ],
    },
  ]

  const dispatch = store.dispatch as ApiActionDispatch

  return mapDeep(routes, (route) => {
    // in the future, we'll also do this:
    // if (route.Component && !route.action && 'action' in route.Component) {
    //   route.action = withActionErrorHandling((route.Component.action as ActionFn)({ api, store }))
    // }
    // if (route.Component && !route.loader && 'loader' in route.Component) {
    //   route.loader = withLoaders((route.Component.loader as LoaderFn)(api))
    // }
    // Legacy route: fall back to a loader that runs all prefetches
    if (route.Component && !route.loader) {
      route.loader = withLoaders(async ({ request, params }) => {
        const url = new URL(request.url)
        const routerProps: RouteComponentProps = {
          params,
          state: store.getState(),
          components: route.Component ? [route.Component] : [],
          location: {
            pathname: url.pathname,
            search: url.search,
            hash: url.hash,
            action: typeof window !== 'undefined' ? window.getNavigationType?.() : ('PUSH' as NavigationType),
            query: parseUrlQuery(url.search),
          },
        }
        await runAllViewStoreUpdates({
          routerProps,
          store,
          runningPreliminaryUpdates: [
            state.get('navigation') ? Promise.resolve() : dispatch(loadNavigation({ locale })),
          ],
          runningIndependentUpdates: [
            state.get('legalPages') ? Promise.resolve() : dispatch(loadLegalPages({ locale })),
            state.get('footerPages') ? Promise.resolve() : dispatch(loadFooterPages({ locale })),
            state.get('scriptTags') ? Promise.resolve() : dispatch(getScriptTags()),
            state.get('tax') ? Promise.resolve() : dispatch(getTax()),
          ],
        })
        return null
      })
    }
    // `route.handle.Component` is used in the Editor component.
    route.handle = { ...route.handle, Component: route.Component }
    return route
  })
}

// work around not being able to call nested route loaders sequentially
// see https://github.com/remix-run/react-router/discussions/9564
function withLoaders(...loaders: LoaderFunction[]): LoaderFunction {
  return async (args: LoaderFunctionArgs) => {
    try {
      for (const loader of loaders) {
        const returnValue = await loader(args)
        if (returnValue) return returnValue
      }
      return null
    } catch (exception) {
      if (isRedirectResponse(exception)) {
        // Let React Router handle the redirect
        return exception
      } else {
        sendErrorResponse(exception)
      }
    }
  }
}

function sendErrorResponse(err: Error) {
  if (isAxiosError(err)) {
    throw new Response(err.response?.statusText || 'Internal Server Error', { status: err.response?.status || 500 })
  } else if (err instanceof Response) {
    throw err
  }
  throw new Response('Internal Server Error', { status: 500 })
}

function isRedirectResponse(response: Response) {
  return response.status >= 300 && response.status < 400 && response.headers.has('Location')
}
